import { snapshotCommitFiles } from "./stepTrees";
import { loadCommitFileContents } from "./commitFileRows";
import { refreshConnectorUsages } from "./refreshConnectorUsages";
import { type Transaction } from "@rocicorp/zero";
import { userFacingError } from "./authorization";
import { syncAllFacets, writeCommitFile } from "./commitFiles";
import { zql } from "./generatedDrizzleSchema";
import { generateId } from "./id";
import type { CreatorAttribution } from "./rbac/roles";
import { stepDirFromPath, tryParseStepConfig } from "./stepConfig";
import {
  type CommitStep,
  hasConflictMarkers,
  ROOT_DIR,
  threeWayMerge,
} from "./threeWayMerge";

export type ThreeWayMergePlan = {
  result: ReturnType<typeof threeWayMerge>;
  baseSteps: CommitStep[];
  mainSteps: CommitStep[];
  branchSteps: CommitStep[];
};

export function commitStepsFromFiles(
  files: readonly {
    path: string;
    blob?: { content: string } | null;
  }[]
): CommitStep[] {
  const stepMap = new Map<string, { path: string; content: string }[]>();
  for (const f of files) {
    if (!f.blob) continue;
    const dir = stepDirFromPath(f.path) ?? ROOT_DIR;
    const relativePath =
      dir === ROOT_DIR ? f.path : f.path.substring(dir.length + 1);
    const stepFiles = stepMap.get(dir) ?? [];
    stepFiles.push({ path: relativePath, content: f.blob.content });
    stepMap.set(dir, stepFiles);
  }
  return [...stepMap.entries()].map(([dir, files]) => ({ dir, files }));
}

async function loadCommitSteps(
  tx: Transaction,
  commitId: string
): Promise<CommitStep[]> {
  const files = await loadCommitFileContents(tx, commitId);
  return commitStepsFromFiles(files);
}

const wrapOurs = (content: string) =>
  `<<<<<<< your branch\n${content}\n=======\n>>>>>>> main`;
const wrapTheirs = (content: string) =>
  `<<<<<<< your branch\n=======\n${content}\n>>>>>>> main`;

// Markers are the only unresolved state the guards can see, so every conflict
// must leave them in the written tree.
export function mergeWorkflowSteps(
  baseSteps: CommitStep[],
  mainSteps: CommitStep[],
  branchSteps: CommitStep[]
): ReturnType<typeof threeWayMerge> {
  const result = threeWayMerge(baseSteps, mainSteps, branchSteps);
  const fileContent = (steps: CommitStep[], dir: string, path: string) =>
    steps.find((s) => s.dir === dir)?.files.find((f) => f.path === path)
      ?.content;

  for (const conflict of result.deleteConflicts) {
    const index = result.keepSteps.findIndex((s) => s.dir === conflict.dir);
    if (index === -1) continue;
    const [step] = result.keepSteps.splice(index, 1);
    const wrap = conflict.deletedBy === "main" ? wrapOurs : wrapTheirs;
    result.mergedSteps.push({
      dir: step!.dir,
      mergedFiles: step!.files.map((f) => ({
        path: f.path,
        content: wrap(f.content),
      })),
    });
  }

  for (const conflict of result.fileConflicts) {
    const step = result.mergedSteps.find((s) => s.dir === conflict.dir);
    const file = step?.mergedFiles.find((f) => f.path === conflict.path);
    if (!file || hasConflictMarkers(file.content)) continue;
    file.content =
      file.content === fileContent(branchSteps, conflict.dir, conflict.path)
        ? wrapOurs(file.content)
        : wrapTheirs(file.content);
  }

  // A textually clean merge of two valid sides that yields an unparseable
  // config.toml is still a conflict.
  const conflicted = new Set(
    result.fileConflicts.map((c) => `${c.dir}/${c.path}`)
  );
  for (const step of result.mergedSteps) {
    if (step.dir === ROOT_DIR) continue;
    for (const file of step.mergedFiles) {
      if (file.path !== "config.toml") continue;
      if (conflicted.has(`${step.dir}/${file.path}`)) continue;
      if (hasConflictMarkers(file.content)) continue;
      if (tryParseStepConfig(file.content) !== null) continue;
      const branchRaw = fileContent(branchSteps, step.dir, file.path) ?? "";
      const mainRaw = fileContent(mainSteps, step.dir, file.path) ?? "";
      if (!tryParseStepConfig(branchRaw) || !tryParseStepConfig(mainRaw))
        continue;
      file.content = `<<<<<<< your branch\n${branchRaw}\n=======\n${mainRaw}\n>>>>>>> main`;
      result.fileConflicts.push({ dir: step.dir, path: file.path });
    }
  }
  return result;
}

export async function planThreeWayMerge(
  tx: Transaction,
  opts: {
    forkCommitId: string;
    mainHeadCommitId: string;
    branchHeadCommitId: string;
  }
): Promise<ThreeWayMergePlan> {
  const base = await tx.run(
    zql.workflowCommits.where("id", opts.forkCommitId).one()
  );
  if (!base)
    throw userFacingError(
      "This draft’s merge base no longer exists, so live changes can’t be merged into it. Create a new draft from live and reapply your changes."
    );
  const [baseSteps, mainSteps, branchSteps] = await Promise.all([
    loadCommitSteps(tx, opts.forkCommitId),
    loadCommitSteps(tx, opts.mainHeadCommitId),
    loadCommitSteps(tx, opts.branchHeadCommitId),
  ]);
  const result = mergeWorkflowSteps(baseSteps, mainSteps, branchSteps);
  return { result, baseSteps, mainSteps, branchSteps };
}

const commitPath = (dir: string, path: string) =>
  dir === ROOT_DIR ? path : `${dir}/${path}`;

export function hasMergeConflicts(plan: ThreeWayMergePlan): boolean {
  return (
    plan.result.fileConflicts.length > 0 ||
    plan.result.deleteConflicts.length > 0
  );
}

export async function writeMergeCommit(
  tx: Transaction,
  opts: {
    newCommitId: string;
    chatId: string | null;
    workflowId: string;
    branchId: string;
    parentCommitId: string;
    branchHeadCommitId: string;
    creator: CreatorAttribution;
    tenantId: string;
    message: string | null;
    now: number;
    plan: ThreeWayMergePlan;
  }
): Promise<void> {
  await tx.mutate.workflowCommits.insert({
    id: opts.newCommitId,
    workflowId: opts.workflowId,
    branchId: opts.branchId,
    parentCommitId: opts.parentCommitId,
    chatId: opts.chatId,
    message: opts.message,
    createdAt: opts.now,
    ...opts.creator,
    tenantId: opts.tenantId,
  });

  for (const step of opts.plan.result.keepSteps) {
    for (const file of step.files) {
      await writeCommitFile(
        { tx, commitId: opts.newCommitId, tenantId: opts.tenantId },
        commitPath(step.dir, file.path),
        file.content
      );
    }
  }

  for (const merged of opts.plan.result.mergedSteps) {
    for (const file of merged.mergedFiles) {
      await writeCommitFile(
        { tx, commitId: opts.newCommitId, tenantId: opts.tenantId },
        commitPath(merged.dir, file.path),
        file.content
      );
    }
  }

  await syncAllFacets({
    tx,
    commitId: opts.newCommitId,
    tenantId: opts.tenantId,
  });
  if (opts.message !== null)
    await snapshotCommitFiles({
      tx,
      commitId: opts.newCommitId,
      tenantId: opts.tenantId,
    });
}

/** Returns the branch's effective head; throws when the merge would conflict. */
export async function mergeMainIfBehind(
  tx: Transaction,
  opts: {
    branch: {
      id: string;
      workflowId: string;
      headCommitId: string | null;
      forkCommitId: string | null;
    };
    creator: CreatorAttribution;
    tenantId: string;
  }
): Promise<string | null> {
  const { branch } = opts;
  if (!branch.forkCommitId || !branch.headCommitId) return branch.headCommitId;
  const [mainBranch] = await tx.run(
    zql.workflowBranches
      .where("workflowId", branch.workflowId)
      .where("name", "main")
  );
  if (
    !mainBranch?.headCommitId ||
    branch.forkCommitId === mainBranch.headCommitId
  ) {
    return branch.headCommitId;
  }

  const plan = await planThreeWayMerge(tx, {
    forkCommitId: branch.forkCommitId,
    mainHeadCommitId: mainBranch.headCommitId,
    branchHeadCommitId: branch.headCommitId,
  });
  if (hasMergeConflicts(plan)) {
    throw userFacingError(
      "Main has changes that conflict with this branch. Pull main in to resolve them.",
      { code: "behind-with-conflicts" }
    );
  }

  const mergeCommitId = generateId();
  await writeMergeCommit(tx, {
    newCommitId: mergeCommitId,
    chatId: null,
    workflowId: branch.workflowId,
    branchId: branch.id,
    parentCommitId: branch.headCommitId,
    branchHeadCommitId: branch.headCommitId,
    creator: opts.creator,
    tenantId: opts.tenantId,
    message: "Merged main",
    now: Date.now(),
    plan,
  });
  await tx.mutate.workflowBranches.update({
    id: branch.id,
    headCommitId: mergeCommitId,
    forkCommitId: mainBranch.headCommitId,
  });
  await refreshConnectorUsages(tx, mergeCommitId);
  return mergeCommitId;
}

export function filesBySide(
  baseSteps: CommitStep[],
  otherSteps: CommitStep[]
): { added: string[]; modified: string[]; removed: string[] } {
  const baseMap = new Map(baseSteps.map((s) => [s.dir, s]));
  const otherMap = new Map(otherSteps.map((s) => [s.dir, s]));
  const added: string[] = [];
  const modified: string[] = [];
  const removed: string[] = [];
  for (const [dir, os] of otherMap) {
    if (dir === ROOT_DIR) continue;
    const bs = baseMap.get(dir);
    if (!bs) {
      added.push(dir);
    } else if (!stepFilesEqual(bs.files, os.files)) {
      modified.push(dir);
    }
  }
  for (const dir of baseMap.keys()) {
    if (dir === ROOT_DIR) continue;
    if (!otherMap.has(dir)) removed.push(dir);
  }
  const baseRoot = new Map(
    (baseMap.get(ROOT_DIR)?.files ?? []).map((f) => [f.path, f.content])
  );
  const otherRoot = new Map(
    (otherMap.get(ROOT_DIR)?.files ?? []).map((f) => [f.path, f.content])
  );
  for (const [path, content] of otherRoot) {
    const baseContent = baseRoot.get(path);
    if (baseContent === undefined) added.push(path);
    else if (baseContent !== content) modified.push(path);
  }
  for (const path of baseRoot.keys()) {
    if (!otherRoot.has(path)) removed.push(path);
  }
  return { added, modified, removed };
}

function stepFilesEqual(
  a: { path: string; content: string }[],
  b: { path: string; content: string }[]
): boolean {
  if (a.length !== b.length) return false;
  const aMap = new Map(a.map((f) => [f.path, f.content]));
  for (const bf of b) {
    if (aMap.get(bf.path) !== bf.content) return false;
  }
  return true;
}
